import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { type Transition, type Variants, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { fcArrowLeftChevron, fcArrowRightChevron } from '@seeqdev/icons/seeq';
import Button from '../Button/Button';
import { colors } from '../theme/colors.stylex';
import type { CarouselProps } from './Carousel.types';

const fadeIn = stylex.keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });
const styles = stylex.create({
  root: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  row: { display: 'flex', height: 'max-content', width: '100%', alignItems: 'center', gap: '0.25rem' },
  viewport: { height: '100%', width: '100%', overflow: 'hidden' },
  slide: { width: '100%' },
  arrow: { animationName: fadeIn, animationDuration: '0.3s', animationTimingFunction: 'ease-in-out' },
  hidden: { visibility: 'hidden' },
  indicators: { marginTop: '0.5rem', display: 'flex', gap: '0.25rem' },
  indicator: {
    height: '0.5rem',
    width: '0.5rem',
    cursor: 'pointer',
    borderRadius: '9999px',
    backgroundColor: colors.textMuted,
  },
  selected: { backgroundColor: colors.accent },
});

/**
 * Carousel:
 *  - Easily create a carousel with custom slides.
 *  - Supports automatic sliding, navigation arrows, and slide indicators.
 */
const Carousel = ({
  testId = 'carousel-id',
  activeIndex = 0,
  extraClassNames = '',
  xstyle,
  onSlide = () => {},
  autoSlide,
  showIndicators = true,
  continuous = true,
  interval = 4000,
  prevIcon = fcArrowLeftChevron,
  nextIcon = fcArrowRightChevron,
  showArrows = true,
  iconExtraClassNames = '',
  iconXstyle,
  carouselItems,
}: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(activeIndex);

  const changeSlide = (nextIndex: number) => {
    const newIndex = (nextIndex + carouselItems.length) % carouselItems.length;
    setCurrentIndex(newIndex);
    onSlide(newIndex, newIndex > currentIndex ? 'right' : 'left');
  };

  useEffect(() => {
    if (autoSlide) {
      const slideInterval = setInterval(() => {
        if (continuous || currentIndex < carouselItems.length - 1) {
          changeSlide(currentIndex + 1);
        } else {
          clearInterval(slideInterval);
        }
      }, interval);
      return () => clearInterval(slideInterval);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSlide, currentIndex, continuous, interval, carouselItems.length]);

  useEffect(() => {
    if (activeIndex !== currentIndex) {
      setCurrentIndex(activeIndex);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const onBackClick = () => {
    if (currentIndex === 0 && !continuous) {
      return;
    }
    let prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      prevIndex = carouselItems.length - 1;
    }
    changeSlide(prevIndex);
  };

  const onForwardClick = () => {
    if (currentIndex === carouselItems.length - 1) {
      if (!continuous) {
        return;
      }
      changeSlide(0);
    } else {
      changeSlide(currentIndex + 1);
    }
  };

  const springTransition: Transition = {
    type: 'spring',
    damping: 20,
    stiffness: 100,
  };

  const slideVariants: Variants = {
    initial: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    animate: {
      x: 0,
      opacity: 1,
      transition: springTransition,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0,
      transition: springTransition,
    }),
  };

  const rootProps = stylex.props(styles.root, xstyle);
  return (
    <div data-testid={testId} {...rootProps} className={clsx(rootProps.className, extraClassNames)}>
      <div {...stylex.props(styles.row)}>
        {showArrows && (
          <Button
            icon={prevIcon}
            size="lg"
            variant="no-border"
            testId="carousel-prev"
            extraClassNames={iconExtraClassNames}
            xstyle={[styles.arrow, currentIndex === 0 && !continuous && styles.hidden, iconXstyle]}
            onClick={onBackClick}
          />
        )}
        <div {...stylex.props(styles.viewport)}>
          <motion.div
            key={currentIndex}
            custom={1} // Direction of animation (1 for forward, -1 for backward)
            initial="initial"
            animate="animate"
            exit="exit"
            variants={slideVariants}
            {...stylex.props(styles.slide)}>
            {carouselItems[currentIndex]}
          </motion.div>
        </div>
        {showArrows && (
          <Button
            icon={nextIcon}
            testId="carousel-next"
            size="lg"
            variant="no-border"
            extraClassNames={iconExtraClassNames}
            xstyle={[
              styles.arrow,
              currentIndex === carouselItems.length - 1 && !continuous && styles.hidden,
              iconXstyle,
            ]}
            onClick={onForwardClick}
          />
        )}
      </div>
      {showIndicators && (
        <div {...stylex.props(styles.indicators)}>
          {carouselItems.map((_, i) => (
            <div
              key={i}
              {...stylex.props(styles.indicator, i === currentIndex && styles.selected)}
              onClick={() => changeSlide(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
